import { HeadingMedium, LinkButton, LockIcon, theme, useToast } from '@arnold/common';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import PublicLayout from '../../components/Common/PublicLayout';
import { useSendMagicLinkMutation } from '../../generated/hooks';
import auth from '../../lib/auth';
import storage from '../../lib/storage';
import { RoundLogo } from '../Login/Common';
import LoginAside from '../Login/LoginAside';
import { GrantConsentForm } from '../Login/GrantConsentForm';

enum State {
  INIT,
  EXPIRED,
  ERROR,
  MISSING_CONSENT,
}

const TextWrap = styled.div`
  margin-bottom: ${theme.spacing.h};
  text-align: center;
`;

const LoginWithMagicLinkConfirmScreen = ({ match, history }: RouteComponentProps<any>) => {
  const [t] = useTranslation('loginForm');
  const lang = match.params.lang;
  const detectedLang = lang || storage.getDetectedLanguage()?.substring(0, 2);
  const { addToast } = useToast();
  const [sendMagicLink] = useSendMagicLinkMutation();
  const email = new URLSearchParams(history.location.search).get('email') ?? '';
  const redirect = new URLSearchParams(history.location.search).get('redirect') ?? '';
  const [state, setState] = useState(State.INIT);

  useEffect(() => {
    (async () => {
      try {
        await auth.login(email, undefined, undefined, redirect || '/overview', match.params.accessToken);
      } catch (e: any) {
        setState(e.status === 403 ? State.MISSING_CONSENT : e.status === 418 ? State.EXPIRED : State.ERROR);
      }
    })();
  }, [match.params.accessToken, email, redirect]);

  return (
    <PublicLayout
      basePath={`loginWithMagicLinkConfirm/${match.params.accessToken}`}
      history={history}
      lang={detectedLang}
      aside={<LoginAside />}
    >
      <>
        {state !== State.INIT && (
          <>
            <RoundLogo>
              <LockIcon />
            </RoundLogo>
            <HeadingMedium className="text-center">
              {t(
                state === State.MISSING_CONSENT
                  ? 'grantConsent'
                  : state === State.EXPIRED
                    ? 'magicLinkExpired'
                    : 'magicLinkError',
              )}
            </HeadingMedium>

            {state !== State.MISSING_CONSENT && (
              <>
                <TextWrap>
                  <Trans
                    i18nKey={
                      state === State.EXPIRED ? 'loginForm:magicLinkExpiredText' : 'loginForm:magicLinkErrorText'
                    }
                    values={{ email }}
                  />
                </TextWrap>

                <div className="text-center">
                  <LinkButton
                    data-cy={'admin-send-again-button'}
                    onClick={async () => {
                      try {
                        await sendMagicLink({ variables: { username: email, redirect } });
                        auth.setUserEmail(email);
                      } catch (e) {
                        addToast(t('sendingLinkFailed'));
                      }
                      addToast(t('loginForm:modalTitle'));
                    }}
                  >
                    {t('recoverPassword:sendAgain')}
                  </LinkButton>
                </div>
              </>
            )}
            {state === State.MISSING_CONSENT && (
              <GrantConsentForm username={email} accessToken={match.params.accessToken} redirect={redirect} />
            )}
          </>
        )}
      </>
    </PublicLayout>
  );
};

export default LoginWithMagicLinkConfirmScreen;
